import React, { useState, useRef, useEffect } from 'react';
import { Button } from 'components/components/atoms';
import { Container } from 'components/shared';
import { useSpring, animated } from 'react-spring';
import styled from 'styled-components';
import tw from 'twin.macro';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import breakpoints from 'utils/breakpoints';
import cx from 'classnames';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { useCountryValue } from 'stores/country';
import LeftArrowIcon from 'components/icons/LeftArrow';
import { useMeasure } from 'react-use';
import config from 'temp/config';
import pageTypes from 'utils/pageTypes';

const Heading = styled.h2`
  @media ${breakpoints.xl} {
    font-size: 2.875rem;
  }
`;

const StyledButton = styled(Button)`
  @media ${breakpoints.md} {
    ${tw`px-4`}
  }
  @media ${breakpoints.lg} {
    width: 12.7rem;
    ${tw`px-0`}
  }
`;

const LeftIconCity = styled.div``;

const StateContainer = styled.div`
  scroll-snap-align: start;
  ${({ isMobile }) =>
    isMobile &&
    `
      border-bottom-width: 2px;
      margin-top: 1.25rem;
      &:nth-child(1) {
        margin-top: 0;
      }
      &:nth-last-child(1) {
        border-bottom-width: 0px;
      }
    `}
`;

const City = styled.a`
  ${tw`flex font-graphie text-lg md:text-base leading-7 md:leading-6 py-1 tracking-normal relative`}
  &:hover {
    font-weight: bold;
  }
  &:hover ${LeftIconCity} {
    opacity: 1;
    right: -1.5rem;
  }
`;

const CityList = styled.div`
  margin-top: 0.2rem;
  margin-bottom: 1rem;
  display: inline-grid;
  column-gap: 5rem;
`;

const getHref = (componentName, city) => {
  if (componentName === 'Market Selector') {
    return city.href;
  } else if (componentName === 'Home Buying Markets') {
    return (city.href || {}).href;
  }
  return '';
};

const MarketSelector = ({ fields, rendering }) => {
  const { componentName } = rendering;

  const [{ country }] = useCountryValue();

  const [ref, { height }] = useMeasure();

  const currentPageType = getFieldValue(fields, 'currentPageType');
  let ctaText = '';
  let marketsDropdown = [];
  const marketSelector = getFieldValue(fields, 'marketSelector') || {};
  const title = marketSelector.title;
  const textStyle = marketSelector.textStyle;

  if (componentName === 'Market Selector') {
    ctaText = marketSelector.ctaText;
    marketsDropdown = getFieldValue(fields, 'marketsDropdown') || [];
  } else if (componentName === 'Home Buying Markets') {
    ctaText = marketSelector.ctaLabel;
    marketsDropdown = marketSelector.homeBuyingMarkets;
  }

  const isWhiteColor = textStyle === 'blue-white';

  const [marketsVisible, toggleMarkets] = useState(false);

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['xs', 'sm'].includes(breakpoint);

  const marketsVisibleStyle = useSpring({
    height: marketsVisible ? height + (isMobile ? 0 : 40) : 0,
  });

  const fade = useSpring({
    opacity: marketsVisible ? 1 : 0,
    delay: marketsVisible ? 200 : 0,
  });

  const fadeMobileButton = useSpring({
    opacity: marketsVisible ? 0 : 1,
    delay: marketsVisible ? 300 : 0,
  });

  const marketsRef = useRef();
  useOnClickOutside(marketsRef, (event) => {
    if (event.srcElement.tagName !== 'HTML') toggleMarkets(false);
  });

  const getSearchLink = (title) => {
    return `${config.sitecoreApiHost}/search?country=${country}&metro=${title}&productType=community`;
  };

  const filteredMarkets = marketsDropdown
    ? marketsDropdown.reduce((acc, city) => {
        if (city.country !== country) {
          return acc;
        }

        const cityExists = acc.find((item) => city.state === item.state);
        if (cityExists) {
          cityExists.cities = [...cityExists.cities, city];
        } else {
          acc.push({
            state: city.state,
            cities: [city],
            country: city.country,
          });
        }
        return acc;
      }, [])
    : marketsDropdown || [];

  if (filteredMarkets < 1 || !title || !ctaText) {
    return null;
  }

  return (
    <section className='w-screen flex flex-col items-center justify-start py-10'>
      <Container ref={marketsRef} className='overflow-auto'>
        <div
          className={cx(
            'flex flex-col items-center justify-between text-white rounded-lg',
            {
              'bg-mattamy-blue border-2 border-mattamy-blue': !isWhiteColor,
              'bg-white border-2 border-action-blue': isWhiteColor,
            },
          )}
        >
          <div
            className={cx(
              'flex flex-col md:flex-row items-center justify-between w-full',
              'pl-30-mattamy md:pl-10 pr-8 pt-30-mattamy rounded-lg',
              { 'pb-30-mattamy': !isMobile || (isMobile && !marketsVisible) },
            )}
          >
            <Heading
              className={cx(
                'text-5xl md:text-4xl tracking-wide font-bold font-trade-gothic-20',
                'text-center md:text-left mt-4 mb-5 md:mb-2 md:mr-8 leading-tight',
                {
                  'text-white': !isWhiteColor,
                  'text-mattamy-blue': isWhiteColor,
                },
              )}
            >
              {title}
            </Heading>
            <div className='w-full md:w-auto flex flex-col md:flex-row items-center justify-center md:justify-end'>
              <div className='w-full'>
                {(!isMobile || (isMobile && !marketsVisible)) && (
                  <animated.div style={isMobile ? fadeMobileButton : {}}>
                    <StyledButton
                      className={cx({
                        'hover:bg-white hover:text-mattamy-blue hover:border-white':
                          textStyle === 'white-blue' || !textStyle,
                      })}
                      color={
                        isWhiteColor
                          ? marketsVisible
                            ? 'mattamy-blue'
                            : 'action-blue'
                          : 'white'
                      }
                      variant={
                        isWhiteColor || isMobile || marketsVisible
                          ? 'filled'
                          : 'outlined'
                      }
                      textColor={
                        isWhiteColor || isMobile || marketsVisible
                          ? 'mattamy-blue'
                          : 'white'
                      }
                      fullWidthMobile
                      onClick={() => toggleMarkets(!marketsVisible)}
                      hoverState
                      buttonDescription={
                        marketsVisible
                          ? 'Collapse Markets Section'
                          : 'Expand this section to select a market and search now.'
                      }
                      aria-expanded={marketsVisible}
                    >
                      {ctaText}
                    </StyledButton>
                  </animated.div>
                )}
              </div>
            </div>
          </div>
          <animated.div style={marketsVisibleStyle} className='w-full'>
            <animated.div
              ref={ref}
              className='w-full flex flex-col'
              active={marketsVisible}
              style={fade}
            >
              {marketsVisible && (
                <div className='lg:flex flex-wrap items-start px-8 md:px-10'>
                  {filteredMarkets.map((state) => (
                    <StateContainer
                      key={state.state}
                      isMobile={isMobile}
                      className={cx('md:w-1/5', {
                        'text-white border-white': !isWhiteColor,
                        'text-mattamy-blue border-mattamy-blue': isWhiteColor,
                      })}
                    >
                      <h3 className='font-graphie md:font-trade-gothic-20 text-xl md:text-3xl w-full leading-9 tracking-tight'>
                        {currentPageType !== pageTypes.DesignStudio ? (
                          <a
                            href={getSearchLink(state.state)}
                            aria-label={state.state}
                          >
                            {state.state}
                          </a>
                        ) : (
                          state.state
                        )}
                      </h3>
                      <CityList className='ml-4 md:ml-0'>
                        {state.cities.map((city) => (
                          <City
                            rel='prefetch'
                            href={getHref(componentName, city)}
                            key={city.city}
                          >
                            <div className='relative'>
                              {city.city}
                              <LeftIconCity className='absolute flex opacity-0 items-center right-0 bottom-0 top-0 transition-all duration-300 ease-out'>
                                <LeftArrowIcon
                                  className={
                                    'transform text-aqua w-3 h-3 rotate-180'
                                  }
                                  id={'MarketSelector-' + city.city}
                                />
                              </LeftIconCity>
                            </div>
                          </City>
                        ))}
                      </CityList>
                    </StateContainer>
                  ))}
                </div>
              )}

              {isMobile && marketsVisible && (
                <div className='w-full md:w-auto flex flex-col md:flex-row items-center justify-center md:justify-end px-30-mattamy mb-10'>
                  <div className='w-full'>
                    <StyledButton
                      color={isWhiteColor ? 'action-blue' : 'white'}
                      variant={'filled'}
                      textColor={isWhiteColor ? 'white' : 'mattamy-blue'}
                      fullWidthMobile
                      onClick={() => toggleMarkets(false)}
                      buttonDescription={'Collapse Markets section'}
                    >
                      {'CLOSE'}
                    </StyledButton>
                  </div>
                </div>
              )}
            </animated.div>
          </animated.div>
        </div>
      </Container>
    </section>
  );
};

export default MarketSelector;
